import { ReactNode, useMemo } from 'react';
import { useQueue } from '@shared/util/hooks/useQueue';
import { Toast } from './Toast';
import { ToastContext } from './ToastContext';
import { QueuedToast, useToastCreator } from './useToastCreator';

export function ToastProvider({ children }: { children: ReactNode }) {
  const queue = useQueue<QueuedToast>();
  const toast = useToastCreator(queue);
  const value = useMemo(() => ({ toast }), [toast]);

  const topContainerMask = {
    maskImage: 'linear-gradient(to top, transparent, black 50%)',
  };
  const bottomContainerMask = {
    maskImage: 'linear-gradient(to bottom, transparent, black 50%)',
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="absolute z-1000 pointer-events-none flex flex-col gap-4 justify-between top-0 bottom-0 right-0 w-full sm:w-[360px] mt-4 md:m-4 md:mt-8 box-border">
        <div className="flex flex-col grow overflow-hidden" style={topContainerMask}>
          {queue.items.filter(isPositionTop).map((t, index) => (
            <ToastRenderer key={t.id} toast={t} index={index} />
          ))}
        </div>
        <div className="flex flex-col-reverse grow overflow-hidden" style={bottomContainerMask}>
          {queue.items.filter(isPositionBottom).map((t, index) => (
            <ToastRenderer key={t.id} toast={t} index={index} />
          ))}
        </div>
      </div>
    </ToastContext.Provider>
  );
}

function isPositionTop(toast: QueuedToast) {
  return toast.position === 'top';
}

function isPositionBottom(toast: QueuedToast) {
  return toast.position === 'bottom';
}

function ToastRenderer({ toast, index }: { toast: QueuedToast; index: number }) {
  const { remove, ...toastProps } = toast;
  return <Toast {...toastProps} onClose={remove} autoClose={toastProps.autoClose && index === 0} />;
}
